import { FC, Fragment } from 'react'

import styled from 'styled-components'

import { ScheduleSummaryProps } from '../..'
import { Divider } from '../Divider'
import {
  DiscountTotalsLine,
  DiscountTotalsLineProps,
} from '../../ThresholdCouponBanner'

import { ScheduleSummaryPriceDetailsItem } from './ScheduleSummaryPriceDetailsItem'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  width: 100%;
  gap: 8px;
`

const DiscountLineSlot = styled.div`
  width: 100%;
`

const Accent = styled(ScheduleSummaryPriceDetailsItem)`
  :first-child {
    color: ${({ theme }) => theme.color.brandGreen};
  }
`

const ROW_AFTER_SUBTOTAL_INDEX = 1

type PriceRow = {
  key: string
  title: string
  price: string
  isAccent?: boolean
  discountLine?: DiscountTotalsLineProps
}

type Props = Pick<
  ScheduleSummaryProps,
  'price' | 'vat' | 'discount' | 'balance' | 'isBalanceHidden'
> & {
  discountLine?: DiscountTotalsLineProps | null
}

const getDiscountRow = (
  discount: string | undefined,
  discountLine: DiscountTotalsLineProps | null | undefined,
): PriceRow | null => {
  if (discountLine) {
    return {
      key: 'discount',
      title: 'Discount',
      price: discountLine.amount,
      discountLine,
    }
  }
  return discount
    ? { key: 'discount', title: 'Discount', price: discount, isAccent: true }
    : null
}

const getPriceRows = ({
  price,
  vat,
  discount,
  balance,
  isBalanceHidden,
  discountLine,
}: Props): PriceRow[] => {
  const subtotal: PriceRow = { key: 'subtotal', title: 'Subtotal', price }
  const balanceRow: PriceRow | null = isBalanceHidden
    ? null
    : { key: 'balance', title: 'Balance', price: balance }
  const vatRow: PriceRow | null = vat
    ? { key: 'vat', title: 'VAT', price: vat }
    : null
  const discountRow: PriceRow | null = getDiscountRow(discount, discountLine)

  const rows = [subtotal, discountRow, balanceRow, vatRow]

  return rows.filter((row): row is PriceRow => Boolean(row))
}

export const ScheduleSummaryPriceDetails: FC<Props> = (props) => {
  const rows = getPriceRows(props)

  return (
    <Container>
      {rows.map(
        ({ key, title, price, isAccent, discountLine: line }, index) => {
          const Item = isAccent ? Accent : ScheduleSummaryPriceDetailsItem
          const isUnderSubtotal = index === ROW_AFTER_SUBTOTAL_INDEX

          return (
            <Fragment key={key}>
              {isUnderSubtotal && <Divider data-testid='summary-divider' />}
              {line ? (
                <DiscountLineSlot>
                  <DiscountTotalsLine {...line} />
                </DiscountLineSlot>
              ) : (
                <Item title={title} price={price} />
              )}
            </Fragment>
          )
        },
      )}
    </Container>
  )
}
